"use client";

import { useEffect, useRef, useState } from "react";
import { API_BASE } from "@/lib/api";
import Turnstile from "@/components/ui/Turnstile";

/* No placeholder option: it only repeated the "Volume" label above the field,
   and it was the longest string here so it truncated in the half-width select.
   Defaulting to "Not sure yet" means the field is always answerable and never
   blocks a submission. */
const TIERS = [
  { value: "pro", label: "Just me — a handful" },
  { value: "business", label: "A team — dozens" },
  { value: "enterprise", label: "A company — hundreds or more" },
  { value: "not_sure", label: "Not sure yet" },
];
const TIER_DEFAULT = "not_sure";

type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_path?: string;
};

export function PartnerLeadForm({
  campaignSlug,
  partnerName,
}: {
  campaignSlug: string;
  partnerName: string;
}) {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);

  // Inlined at build time. When it's absent the Turnstile widget renders
  // nothing, no token is ever minted, and the API rejects every submission with
  // 400 — so say so up front and offer a route that works, rather than letting
  // someone fill the whole form and hit a wall.
  const captchaConfigured = Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY);

  // Captured once on mount. These are unrecoverable after the visitor navigates,
  // which is why the partner's attribution has to be grabbed here and now.
  const attribution = useRef<Attribution>({});
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const pick = (k: string) => q.get(k) || undefined;
    attribution.current = {
      utm_source: pick("utm_source"),
      utm_medium: pick("utm_medium"),
      utm_campaign: pick("utm_campaign"),
      utm_content: pick("utm_content"),
      utm_term: pick("utm_term"),
      referrer: document.referrer || undefined,
      landing_path: window.location.pathname + window.location.search,
    };
  }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    const form = new FormData(e.currentTarget);

    const payload = {
      name: String(form.get("name") || "").trim(),
      email: String(form.get("email") || "").trim(),
      company: String(form.get("company") || "").trim() || null,
      phone: String(form.get("phone") || "").trim() || null,
      message: String(form.get("message") || "").trim() || null,
      tier_interest: String(form.get("tier_interest") || "") || null,
      campaign_slug: campaignSlug,
      turnstile_token: turnstileToken,
      website: String(form.get("website") || ""), // honeypot
      ...attribution.current,
    };

    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // Our own API returns a human-readable `detail` on 4xx (e.g. "CAPTCHA
        // verification is required."). Showing it beats a generic string that
        // sends the visitor — and whoever they report it to — hunting blind.
        // 5xx detail is deliberately generic server-side, so don't rely on it.
        let detail = "";
        if (res.status < 500) {
          try {
            const body = await res.json();
            if (typeof body?.detail === "string") detail = body.detail;
          } catch {
            /* non-JSON error body */
          }
        }
        setError(
          res.status === 429
            ? "That's a few too many attempts — please try again shortly."
            : detail ||
                "Something went wrong. Please try again, or email hello@signforge.io."
        );
        setResetKey((k) => k + 1);
        setTurnstileToken(null);
        return;
      }
      setSent(true);
    } catch {
      setError("Couldn't reach us just now. Please try again.");
      setResetKey((k) => k + 1);
      setTurnstileToken(null);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-8 py-12 text-center">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-brand-100">
          <svg className="h-6 w-6 text-brand-700" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <h3 className="font-display text-xl font-bold text-ink tracking-tight">Thank you — that&apos;s with us.</h3>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
          {partnerName} will be in touch shortly. We&apos;ve sent a short confirmation
          to your inbox so you have this in writing.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink " +
    "placeholder:text-slate-400 transition-colors focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600/25";

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {/* Honeypot — off-screen, unfocusable, never announced. Bots fill it; people can't. */}
      <div aria-hidden="true" className="absolute left-[-9999px] top-0 h-0 w-0 overflow-hidden">
        <label htmlFor="website">Website</label>
        <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
      </div>

      {/* Field order is deliberate. Email holds the longest value of any field
          here, so it gets a full row of its own; Name and Company are short and
          pair comfortably. Same three rows as pairing name+email, so the form is
          no taller — the width just goes where it's actually needed. */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-xs font-medium text-slate-600">
            Your name <span className="text-brand-600">*</span>
          </label>
          <input id="name" name="name" required maxLength={255} autoComplete="name" className={inputCls} />
        </div>
        <div>
          <label htmlFor="company" className="mb-1.5 block text-xs font-medium text-slate-600">
            Company
          </label>
          <input id="company" name="company" maxLength={255} autoComplete="organization" className={inputCls} />
        </div>
      </div>

      <div>
        <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-slate-600">
          Work email <span className="text-brand-600">*</span>
        </label>
        <input id="email" name="email" type="email" required autoComplete="email" className={inputCls} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="phone" className="mb-1.5 block text-xs font-medium text-slate-600">
            Phone
          </label>
          <input id="phone" name="phone" maxLength={50} autoComplete="tel" className={inputCls} />
        </div>
        <div>
          <label htmlFor="tier_interest" className="mb-1.5 block text-xs font-medium text-slate-600">
            Volume
          </label>
          <select
            id="tier_interest"
            name="tier_interest"
            defaultValue={TIER_DEFAULT}
            className={inputCls}
          >
            {TIERS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="message" className="mb-1.5 block text-xs font-medium text-slate-600">
          Additional details
        </label>
        <textarea
          id="message"
          name="message"
          rows={3}
          maxLength={5000}
          placeholder="Tell us a little about your documents, your team, and what you're using today."
          className={`${inputCls} resize-y`}
        />
      </div>

      {captchaConfigured ? (
        <Turnstile
          onVerify={setTurnstileToken}
          onExpire={() => setTurnstileToken(null)}
          onError={() => setTurnstileToken(null)}
          theme="light"
          resetKey={resetKey}
        />
      ) : (
        <p className="rounded-lg bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">
          Verification isn&apos;t available on this environment, so the form
          can&apos;t be submitted here. Please email{" "}
          <a href="mailto:hello@signforge.io" className="font-semibold underline">
            hello@signforge.io
          </a>{" "}
          instead.
        </p>
      )}

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      <button
        type="submit"
        // Blocked until the challenge is solved: submitting without a token is
        // a guaranteed 400, and a failed submit resets the widget, so the
        // visitor would have to solve it again to retry.
        disabled={busy || !captchaConfigured || !turnstileToken}
        className="w-full rounded-lg bg-brand-600 px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="relative">{busy ? "Sending…" : "Request a demo"}</span>
      </button>

      {captchaConfigured && !turnstileToken && !busy && (
        <p className="-mt-2 text-center text-xs text-slate-400">
          Complete the verification above to enable sending.
        </p>
      )}

      {/* Disclosure at the point of collection — the part that actually matters. */}
      <p className="text-center text-xs leading-relaxed text-slate-400">
        By submitting this form, SignForge and {partnerName} may contact you about
        your enquiry. We don&apos;t sell your data, and you can ask us to delete it
        at any time.
      </p>
    </form>
  );
}
